// src/pages/UserRecruiterReport.jsx

import React, { useEffect, useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx";
import dayjs from "dayjs";
import { FaCog } from "react-icons/fa";
import { getData } from "../../services/FetchNodeservices";

export default function UserRecruiterReportTailwind() {
  // ======================================================
  // DATA
  // ======================================================

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  // ======================================================
  // FILTERS
  // ======================================================

  const [searchText, setSearchText] = useState("");
  const [filterDateOption, setFilterDateOption] = useState("");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");

  const [filterPosition, setFilterPosition] = useState("");
  const [filterCompany, setFilterCompany] = useState("");
  const [filterRecruiter, setFilterRecruiter] = useState("");
  const [filterStatus, setFilterStatus] = useState("");

  // ======================================================
  // SELECTION
  // ======================================================

  const [selectedIds, setSelectedIds] = useState(new Set());

  // ======================================================
  // PAGINATION
  // ======================================================

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // ======================================================
  // COLUMNS
  // ======================================================

  const columns = [
    {
      key: "date",
      label: "Date",
    },
    {
      key: "candidateName",
      label: "Candidate",
    },
    {
      key: "email",
      label: "Email",
    },
    {
      key: "phone",
      label: "Phone",
    },
    {
      key: "positionName",
      label: "Position",
    },
    {
      key: "company",
      label: "Company",
    },
    {
      key: "recruiter",
      label: "Recruiter",
    },
    {
      key: "status",
      label: "Status",
    },
  ];

  const [columnVisibility, setColumnVisibility] = useState({
    date: true,
    candidateName: true,
    email: true,
    phone: true,
    positionName: true,
    company: true,
    recruiter: true,
    status: true,
  });

  const [openColumns, setOpenColumns] = useState(false);
  const columnRef = useRef();

  // ======================================================
  // NORMALIZE VALUE
  // IMPORTANT FOR FILTERING
  // ======================================================

  const normalize = (value) => {
    return String(value ?? "")
      .trim()
      .replace(/\s+/g, " ")
      .toLowerCase();
  };

  // ======================================================
  // CLOSE COLUMN DROPDOWN WHEN CLICKING OUTSIDE
  // ======================================================

  useEffect(() => {
    const handler = (e) => {
      if (
        columnRef.current &&
        !columnRef.current.contains(e.target)
      ) {
        setOpenColumns(false);
      }
    };

    document.addEventListener("mousedown", handler);

    return () => {
      document.removeEventListener("mousedown", handler);
    };
  }, []);

  // ======================================================
  // LOAD ONLY USER-WISE DATA
  // ======================================================

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const user = JSON.parse(
          localStorage.getItem("USER") || "null"
        );

        const userId = user?.id;

        if (!userId) {
          console.warn("User ID not found in localStorage");
          setData([]);
          return;
        }

        const res = await getData(
          "recruiter-report/fetch?user_id=" + userId
        );

        console.log("Recruiter Report API Response:", res);

        if (res?.status) {
          const responseData = Array.isArray(res.data)
            ? res.data
            : [];

          /*
           * Normalize important string fields once when data loads.
           *
           * Example:
           * "Khushi Mishra "
           * becomes
           * "Khushi Mishra"
           */

          const cleanedData = responseData.map((row) => ({
            ...row,

            candidateName:
              row.candidateName != null
                ? String(row.candidateName).trim()
                : "",

            email:
              row.email != null
                ? String(row.email).trim()
                : "",

            phone:
              row.phone != null
                ? String(row.phone).trim()
                : "",

            positionName:
              row.positionName != null
                ? String(row.positionName).trim()
                : "",

            company:
              row.company != null
                ? String(row.company).trim()
                : "",

            recruiter:
              row.recruiter != null
                ? String(row.recruiter).trim()
                : "",

            status:
              row.status != null
                ? String(row.status).trim()
                : "",
          }));

          setData(cleanedData);
        } else {
          console.warn(
            "Recruiter report API returned unsuccessful response"
          );

          setData([]);
        }
      } catch (err) {
        console.error(
          "Recruiter Report Fetch Error:",
          err
        );

        setData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // ======================================================
  // UNIQUE DROPDOWN VALUES
  // ======================================================

  const uniqueValues = (field) => {
    const values = data
      .map((row) => String(row?.[field] ?? "").trim())
      .filter(Boolean);

    return Array.from(new Set(values)).sort((a, b) =>
      a.localeCompare(b)
    );
  };

  // ======================================================
  // DATE FILTER
  // ======================================================

  const isDateInRange = (rowDate) => {
    // No date filter
    if (!filterDateOption) {
      return true;
    }

    if (!rowDate) {
      return false;
    }

    const date = dayjs(rowDate);

    if (!date.isValid()) {
      return false;
    }

    const today = dayjs();

    switch (filterDateOption) {
      case "Today":
        return date.isSame(today, "day");

      case "Yesterday":
        return date.isSame(
          today.subtract(1, "day"),
          "day"
        );

      case "Last Week":
        return (
          date.isAfter(
            today.subtract(7, "day").startOf("day")
          ) &&
          date.isBefore(today.endOf("day"))
        );

      case "Last Month":
        return (
          date.isAfter(
            today.subtract(1, "month").startOf("day")
          ) &&
          date.isBefore(today.endOf("day"))
        );

      case "Custom": {
        let fromOK = true;
        let toOK = true;

        if (customFrom) {
          const fromDate = dayjs(customFrom).startOf("day");

          fromOK =
            date.isSame(fromDate, "day") ||
            date.isAfter(fromDate);
        }

        if (customTo) {
          const toDate = dayjs(customTo).endOf("day");

          toOK =
            date.isSame(toDate, "day") ||
            date.isBefore(toDate);
        }

        return fromOK && toOK;
      }

      default:
        return true;
    }
  };

  // ======================================================
  // FILTER + SEARCH
  // ======================================================

  const filteredData = useMemo(() => {
    const search = normalize(searchText);

    const normalizedPosition = normalize(filterPosition);
    const normalizedCompany = normalize(filterCompany);
    const normalizedRecruiter = normalize(filterRecruiter);
    const normalizedStatus = normalize(filterStatus);

    return data.filter((row) => {
      // --------------------------------------------------
      // SEARCH
      // --------------------------------------------------

      const searchMatch =
        !search ||
        Object.values(row)
          .filter(
            (value) =>
              value !== null &&
              value !== undefined &&
              value !== ""
          )
          .some((value) =>
            normalize(value).includes(search)
          );

      if (!searchMatch) {
        return false;
      }

      // --------------------------------------------------
      // DATE
      // --------------------------------------------------

      if (!isDateInRange(row.date)) {
        return false;
      }

      // --------------------------------------------------
      // POSITION
      // --------------------------------------------------

      if (
        normalizedPosition &&
        normalize(row.positionName) !== normalizedPosition
      ) {
        return false;
      }

      // --------------------------------------------------
      // COMPANY
      // --------------------------------------------------

      if (
        normalizedCompany &&
        normalize(row.company) !== normalizedCompany
      ) {
        return false;
      }

      // --------------------------------------------------
      // RECRUITER
      // IMPORTANT FIX
      // --------------------------------------------------

      if (
        normalizedRecruiter &&
        normalize(row.recruiter) !== normalizedRecruiter
      ) {
        return false;
      }

      // --------------------------------------------------
      // STATUS
      // --------------------------------------------------

      if (
        normalizedStatus &&
        normalize(row.status) !== normalizedStatus
      ) {
        return false;
      }

      return true;
    });
  }, [
    data,
    searchText,
    filterDateOption,
    customFrom,
    customTo,
    filterPosition,
    filterCompany,
    filterRecruiter,
    filterStatus,
  ]);

  // ======================================================
  // PAGINATION
  // ======================================================

  const paginatedData = useMemo(() => {
    const start = page * rowsPerPage;
    const end = start + rowsPerPage;

    return filteredData.slice(start, end);
  }, [
    filteredData,
    page,
    rowsPerPage,
  ]);

  // ======================================================
  // SELECTION
  // ======================================================

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);

      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }

      return next;
    });
  };

  // ======================================================
  // SELECT ALL ON CURRENT PAGE
  // ======================================================

  const toggleSelectAllOnPage = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);

      const allSelected =
        paginatedData.length > 0 &&
        paginatedData.every((row) =>
          next.has(row.candidate_id)
        );

      paginatedData.forEach((row) => {
        if (allSelected) {
          next.delete(row.candidate_id);
        } else {
          next.add(row.candidate_id);
        }
      });

      return next;
    });
  };

  // ======================================================
  // EXPORT HELPERS
  // ======================================================

  const prepareExport = () => {
    const rows =
      selectedIds.size > 0
        ? data.filter((row) =>
            selectedIds.has(row.candidate_id)
          )
        : filteredData;

    return rows.map((row) => {
      const obj = {};

      columns.forEach((column) => {
        if (!columnVisibility[column.key]) {
          return;
        }

        if (column.key === "date") {
          obj[column.label] = row.date
            ? dayjs(row.date).format("DD-MM-YYYY")
            : "";
        } else {
          obj[column.label] =
            row[column.key] ?? "";
        }
      });

      return obj;
    });
  };

  // ======================================================
  // EXPORT EXCEL
  // ======================================================

  const exportToExcel = () => {
    const rows = prepareExport();

    if (!rows.length) {
      alert("No records available to export.");
      return;
    }

    const ws =
      XLSX.utils.json_to_sheet(rows);

    const wb =
      XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(
      wb,
      ws,
      "Recruiter Report"
    );

    XLSX.writeFile(
      wb,
      "recruiter_report.xlsx"
    );
  };

  // ======================================================
  // CLEAR FILTERS
  // ======================================================

  const clearFilters = () => {
    setSearchText("");
    setFilterDateOption("");
    setCustomFrom("");
    setCustomTo("");

    setFilterPosition("");
    setFilterCompany("");
    setFilterRecruiter("");
    setFilterStatus("");

    setSelectedIds(new Set());
    setPage(0);
  };

  // ======================================================
  // PAGINATION INFO
  // ======================================================

  const totalFiltered = filteredData.length;

  const startRow =
    totalFiltered === 0
      ? 0
      : page * rowsPerPage + 1;

  const endRow = Math.min(
    (page + 1) * rowsPerPage,
    totalFiltered
  );

  // ======================================================
  // STATUS BADGE
  // ======================================================

  const statusBadge = (status) => {
    const s = normalize(status);

    let cls =
      "bg-slate-100 text-slate-700 ring-slate-200";

    if (
      s.includes("shortlist") ||
      s.includes("selected")
    ) {
      cls =
        "bg-emerald-50 text-emerald-700 ring-emerald-200";
    } else if (s.includes("reject")) {
      cls =
        "bg-rose-50 text-rose-700 ring-rose-200";
    } else if (s.includes("pending")) {
      cls =
        "bg-amber-50 text-amber-800 ring-amber-200";
    } else if (s.includes("interview")) {
      cls =
        "bg-indigo-50 text-indigo-700 ring-indigo-200";
    }

    return (
      <span
        className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ring-1 ${cls}`}
      >
        {status || "-"}
      </span>
    );
  };

  // ======================================================
  // SELECT ALL STATE
  // ======================================================

  const allOnPageSelected =
    paginatedData.length > 0 &&
    paginatedData.every((row) =>
      selectedIds.has(row.candidate_id)
    );

  // ======================================================
  // VISIBLE COLUMN COUNT
  // ======================================================

  const visibleColumnCount =
    columns.filter(
      (column) =>
        columnVisibility[column.key]
    ).length + 1;

  // ======================================================
  // RENDER
  // ======================================================

  return (
    <div className="w-full">
      {/* ==================================================
          HEADER
      ================================================== */}

      <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-base font-extrabold text-gray-800 uppercase tracking-wider">
            Recruiter Report
          </h2>

          <div className="mt-1 flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider">
            <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-gray-600">
              Total: {filteredData.length}
            </span>

            {selectedIds.size > 0 && (
              <span className="inline-flex items-center rounded-full bg-indigo-50 px-2 py-0.5 text-indigo-650">
                Selected: {selectedIds.size}
              </span>
            )}
          </div>
        </div>

        {/* ACTION BUTTONS */}

        <div className="flex gap-2">
          <button
            onClick={clearFilters}
            className="px-4 py-2 border border-gray-200 text-xs font-bold text-gray-650 hover:bg-gray-50 rounded-xl transition duration-200 active:scale-[0.99]"
          >
            Clear
          </button>

          <button
            onClick={exportToExcel}
            className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold rounded-xl shadow-lg shadow-indigo-100 transition duration-200 active:scale-[0.99]"
          >
            Export Excel 📄
          </button>

          {/* COLUMNS */}

          <div
            className="relative"
            ref={columnRef}
          >
            <button
              onClick={() =>
                setOpenColumns((prev) => !prev)
              }
              className="px-4 py-2 bg-white border border-gray-200 text-xs font-bold text-gray-700 rounded-xl shadow-sm hover:bg-gray-50 transition duration-200 active:scale-[0.99] flex items-center gap-2"
            >
              <FaCog />
              Columns
            </button>

            {openColumns && (
              <div className="absolute right-0 mt-2 bg-white p-4 border border-gray-100 rounded-2xl shadow-xl w-60 z-50 max-h-72 overflow-y-auto">
                {columns.map((column) => (
                  <label
                    key={column.key}
                    className="flex gap-2 text-xs py-1.5 cursor-pointer hover:bg-gray-50 items-center font-semibold text-gray-700"
                  >
                    <input
                      type="checkbox"
                      checked={
                        columnVisibility[
                          column.key
                        ]
                      }
                      className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      onChange={() =>
                        setColumnVisibility(
                          (prev) => ({
                            ...prev,
                            [column.key]:
                              !prev[column.key],
                          })
                        )
                      }
                    />

                    {column.label}
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ==================================================
          MAIN CARD
      ================================================== */}

      <div className="border border-gray-100 rounded-2xl overflow-visible bg-white shadow-sm min-h-[480px]">
        {/* ==================================================
            FILTER TOOLBAR
        ================================================== */}

        <div className="border-b border-gray-100 bg-gray-50/20 p-4 relative z-30">
          <div className="flex flex-wrap gap-3 items-center">
            {/* SEARCH */}

            <input
              value={searchText}
              onChange={(e) => {
                setSearchText(e.target.value);
                setPage(0);
              }}
              className="h-10 w-full sm:w-72 px-3 bg-white border border-gray-200 rounded-xl text-xs font-semibold text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-4 focus:ring-indigo-100/80 focus:border-indigo-500 transition duration-200"
              placeholder="Search candidate / email / phone..."
            />

            {/* POSITION */}

            <select
              value={filterPosition}
              onChange={(e) => {
                setFilterPosition(e.target.value);
                setPage(0);
              }}
              className="h-10 px-3 bg-white border border-gray-200 rounded-xl text-xs font-semibold text-gray-700 focus:outline-none focus:ring-4 focus:ring-indigo-100/80 focus:border-indigo-500 transition duration-200 cursor-pointer"
            >
              <option value="">
                All Positions
              </option>

              {uniqueValues(
                "positionName"
              ).map((position) => (
                <option
                  key={position}
                  value={position}
                >
                  {position}
                </option>
              ))}
            </select>

            {/* COMPANY */}

            <select
              value={filterCompany}
              onChange={(e) => {
                setFilterCompany(e.target.value);
                setPage(0);
              }}
              className="h-10 px-3 bg-white border border-gray-200 rounded-xl text-xs font-semibold text-gray-700 focus:outline-none focus:ring-4 focus:ring-indigo-100/80 focus:border-indigo-500 transition duration-200 cursor-pointer"
            >
              <option value="">
                All Companies
              </option>

              {uniqueValues(
                "company"
              ).map((company) => (
                <option
                  key={company}
                  value={company}
                >
                  {company}
                </option>
              ))}
            </select>

            {/* RECRUITER */}

            <select
              value={filterRecruiter}
              onChange={(e) => {
                setFilterRecruiter(e.target.value);
                setPage(0);
              }}
              className="h-10 px-3 bg-white border border-gray-200 rounded-xl text-xs font-semibold text-gray-700 focus:outline-none focus:ring-4 focus:ring-indigo-100/80 focus:border-indigo-500 transition duration-200 cursor-pointer"
            >
              <option value="">
                All Recruiters
              </option>

              {uniqueValues(
                "recruiter"
              ).map((recruiter) => (
                <option
                  key={recruiter}
                  value={recruiter}
                >
                  {recruiter}
                </option>
              ))}
            </select>

            {/* STATUS */}

            <select
              value={filterStatus}
              onChange={(e) => {
                setFilterStatus(e.target.value);
                setPage(0);
              }}
              className="h-10 px-3 bg-white border border-gray-200 rounded-xl text-xs font-semibold text-gray-700 focus:outline-none focus:ring-4 focus:ring-indigo-100/80 focus:border-indigo-500 transition duration-200 cursor-pointer"
            >
              <option value="">
                All Status
              </option>

              {uniqueValues(
                "status"
              ).map((status) => (
                <option
                  key={status}
                  value={status}
                >
                  {status}
                </option>
              ))}
            </select>

            {/* DATE */}

            <select
              value={filterDateOption}
              onChange={(e) => {
                setFilterDateOption(
                  e.target.value
                );

                setPage(0);

                if (
                  e.target.value !==
                  "Custom"
                ) {
                  setCustomFrom("");
                  setCustomTo("");
                }
              }}
              className="h-10 px-3 bg-white border border-gray-200 rounded-xl text-xs font-semibold text-gray-700 focus:outline-none focus:ring-4 focus:ring-indigo-100/80 focus:border-indigo-500 transition duration-200 cursor-pointer"
            >
              <option value="">
                Any Date
              </option>

              <option value="Today">
                Today
              </option>

              <option value="Yesterday">
                Yesterday
              </option>

              <option value="Last Week">
                Last Week
              </option>

              <option value="Last Month">
                Last Month
              </option>

              <option value="Custom">
                Custom
              </option>
            </select>

            {/* CUSTOM DATE */}

            {filterDateOption ===
              "Custom" && (
              <div className="flex gap-2">
                <input
                  type="date"
                  value={customFrom}
                  onChange={(e) => {
                    setCustomFrom(
                      e.target.value
                    );
                    setPage(0);
                  }}
                  className="h-10 px-3 bg-white border border-gray-200 rounded-xl text-xs font-semibold text-gray-700 focus:outline-none focus:ring-4 focus:ring-indigo-100/80 focus:border-indigo-500 transition duration-200"
                />

                <input
                  type="date"
                  value={customTo}
                  onChange={(e) => {
                    setCustomTo(
                      e.target.value
                    );
                    setPage(0);
                  }}
                  className="h-10 px-3 bg-white border border-gray-200 rounded-xl text-xs font-semibold text-gray-700 focus:outline-none focus:ring-4 focus:ring-indigo-100/80 focus:border-indigo-500 transition duration-200"
                />
              </div>
            )}
          </div>
        </div>

        {/* ==================================================
            TABLE
        ================================================== */}

        <div className="overflow-x-auto">
          <table className="min-w-full text-xs">
            <thead>
              <tr className="border-b border-gray-150 bg-gray-50/50 text-[10px] font-bold text-gray-500 uppercase tracking-wider text-left">
                {/* SELECT ALL */}

                <th className="px-4 py-3.5 w-10">
                  <input
                    type="checkbox"
                    checked={
                      allOnPageSelected
                    }
                    onChange={
                      toggleSelectAllOnPage
                    }
                    className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                </th>

                {columns
                  .filter(
                    (column) =>
                      columnVisibility[
                        column.key
                      ]
                  )
                  .map((column) => (
                    <th
                      key={column.key}
                      className="px-4 py-3.5 font-bold whitespace-nowrap"
                    >
                      {column.label}
                    </th>
                  ))}
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-100">
              {/* LOADING */}

              {loading ? (
                <tr>
                  <td
                    colSpan={
                      visibleColumnCount
                    }
                    className="p-8 text-center text-gray-400 font-semibold"
                  >
                    Loading...
                  </td>
                </tr>
              ) : paginatedData.length ===
                0 ? (
                /* EMPTY */

                <tr>
                  <td
                    colSpan={
                      visibleColumnCount
                    }
                    className="py-20 text-center"
                  >
                    <div className="flex flex-col items-center justify-center gap-2 max-w-sm mx-auto">
                      <div className="w-12 h-12 rounded-2xl bg-indigo-50/80 flex items-center justify-center text-indigo-500 text-xl mb-1">
                        🔍
                      </div>

                      <p className="text-sm font-bold text-gray-700">
                        No Recruiter Report
                        Records
                      </p>

                      <p className="text-xs text-gray-400 font-medium">
                        Try adjusting your
                        search query or
                        recruiter filter
                        criteria.
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                /* DATA */

                paginatedData.map(
                  (row) => (
                    <tr
                      key={
                        row.candidate_id
                      }
                      className="hover:bg-indigo-50/10 transition duration-150 text-gray-700"
                    >
                      {/* CHECKBOX */}

                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(
                            row.candidate_id
                          )}
                          onChange={() =>
                            toggleSelect(
                              row.candidate_id
                            )
                          }
                          className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        />
                      </td>

                      {/* DATE */}

                      {columnVisibility
                        .date && (
                        <td className="px-4 py-3 whitespace-nowrap font-medium text-gray-500">
                          {row.date
                            ? dayjs(
                                row.date
                              ).format(
                                "DD-MM-YYYY"
                              )
                            : "-"}
                        </td>
                      )}

                      {/* CANDIDATE */}

                      {columnVisibility
                        .candidateName && (
                        <td className="px-4 py-3 font-bold text-gray-900 whitespace-nowrap">
                          {row.candidateName ||
                            "-"}
                        </td>
                      )}

                      {/* EMAIL */}

                      {columnVisibility
                        .email && (
                        <td className="px-4 py-3 whitespace-nowrap">
                          {row.email || "-"}
                        </td>
                      )}

                      {/* PHONE */}

                      {columnVisibility
                        .phone && (
                        <td className="px-4 py-3 whitespace-nowrap">
                          {row.phone || "-"}
                        </td>
                      )}

                      {/* POSITION */}

                      {columnVisibility
                        .positionName && (
                        <td className="px-4 py-3 whitespace-nowrap font-semibold text-gray-800">
                          {row.positionName ||
                            "-"}
                        </td>
                      )}

                      {/* COMPANY */}

                      {columnVisibility
                        .company && (
                        <td className="px-4 py-3 whitespace-nowrap text-gray-600">
                          {row.company || "-"}
                        </td>
                      )}

                      {/* RECRUITER */}

                      {columnVisibility
                        .recruiter && (
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className="px-2 py-0.5 bg-indigo-50 text-indigo-650 rounded-full font-bold text-[10px]">
                            {row.recruiter ||
                              "-"}
                          </span>
                        </td>
                      )}

                      {/* STATUS */}

                      {columnVisibility
                        .status && (
                        <td className="px-4 py-3 whitespace-nowrap">
                          {statusBadge(
                            row.status
                          )}
                        </td>
                      )}
                    </tr>
                  )
                )
              )}
            </tbody>
          </table>
        </div>

        {/* ==================================================
            PAGINATION
        ================================================== */}

        <div className="border-t border-gray-150 px-4 py-3 flex items-center justify-between bg-white text-xs">
          <div className="text-gray-500 font-medium">
            Showing{" "}
            <span className="font-semibold text-gray-800">
              {startRow}
            </span>
            –
            <span className="font-semibold text-gray-800">
              {endRow}
            </span>{" "}
            of{" "}
            <span className="font-semibold text-gray-800">
              {totalFiltered}
            </span>
          </div>

          <div className="flex items-center gap-2">
            {/* ROWS PER PAGE */}

            <select
              value={rowsPerPage}
              onChange={(e) => {
                setRowsPerPage(
                  Number(e.target.value)
                );
                setPage(0);
              }}
              className="h-8 px-2 border border-gray-200 rounded-xl text-[11px] font-bold text-gray-650 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-150"
            >
              {[10, 20, 50, 100].map(
                (number) => (
                  <option
                    key={number}
                    value={number}
                  >
                    {number}/page
                  </option>
                )
              )}
            </select>

            {/* PREVIOUS */}

            <button
              disabled={page === 0}
              onClick={() =>
                setPage((current) =>
                  Math.max(
                    0,
                    current - 1
                  )
                )
              }
              className="px-3 py-1.5 border border-gray-200 rounded-xl text-xs font-bold text-gray-600 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 active:scale-[0.99] transition duration-200"
            >
              Prev
            </button>

            {/* NEXT */}

            <button
              disabled={
                (page + 1) *
                  rowsPerPage >=
                totalFiltered
              }
              onClick={() =>
                setPage(
                  (current) =>
                    current + 1
                )
              }
              className="px-3 py-1.5 border border-gray-200 rounded-xl text-xs font-bold text-gray-600 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 active:scale-[0.99] transition duration-200"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}